import {
  DESIGN_SCHEMA_VERSION,
  MAX_DESIGN_ITEMS,
  WRIST_MM_MAX,
  WRIST_MM_MIN,
} from "@stonelab/design-core";
import { z } from "zod";

import { millimeterSchema, positiveIntSchema, uuidSchema } from "./primitives.js";

/**
 * 设计文档的传输结构。字段与 `@stonelab/design-core` 的 `DesignDocument` 必须一致，
 * 由 `pnpm check:contracts` 做类型与行为双向核对；两处定义漂移会被检查拦下。
 *
 * 来源（DesignProvenance）不在此结构中：它由服务端在「套用作品」时写入，
 * 客户端提交的负载不得携带，客户端保存设计也不能修改来源。
 */
export const designItemOrientationSchema = z.object({
  flipped: z.boolean(),
  rotationDeg: z.number().finite().min(0).lt(360),
});

export const designItemSchema = z.object({
  instanceId: uuidSchema,
  skuId: uuidSchema,
  specVersion: positiveIntSchema,
  orientation: designItemOrientationSchema,
});

/**
 * 组件数组：数量不得超过上限，且 instanceId 不得重复。
 * 重复校验与 design-core 的 DESIGN_INSTANCE_DUPLICATED 对应，由一致性测试保证同步。
 */
export const designItemsSchema = z
  .array(designItemSchema)
  .max(MAX_DESIGN_ITEMS)
  .refine((items) => new Set(items.map((item) => item.instanceId)).size === items.length, {
    error: "Duplicated instanceId in items.",
  });

export const designDocumentSchema = z.object({
  schemaVersion: z.literal(DESIGN_SCHEMA_VERSION),
  id: uuidSchema,
  revision: z.int().nonnegative(),
  name: z.string(),
  items: designItemsSchema,
  trayId: z.string().min(1),
  targetWristMm: millimeterSchema.min(WRIST_MM_MIN).max(WRIST_MM_MAX).nullable(),
});

export type DesignDocumentPayload = z.infer<typeof designDocumentSchema>;
export type DesignItemPayload = z.infer<typeof designItemSchema>;
export type DesignItemOrientationPayload = z.infer<typeof designItemOrientationSchema>;
